import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Plus, Chalkboard, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Teacher home — list workspaces (classrooms) the user owns, plus a CTA
 * to create a new one. The simplest possible entry surface for Phase 1.
 */
const ClassroomHome = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');

  // Retry helper for transient failures (cold-start timeouts, mobile
  // network blips, brief auth-refresh races). Only retries on network /
  // 5xx errors — 4xx responses (auth / validation) fail fast since they
  // won't improve on retry. Backoff: 400ms → 1200ms.
  const withRetry = async (fn, attempts = 3) => {
    let lastErr;
    for (let i = 0; i < attempts; i += 1) {
      try {
        return await fn();
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        const isTransient = !status || status >= 500;
        if (!isTransient || i === attempts - 1) throw err;
        await new Promise((r) => setTimeout(r, 400 * (i + 1) ** 2));
      }
    }
    throw lastErr;
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // Teachers came in via magic link — preserve passwordless flow
      navigate('/teachers/start?redirect=/classroom');
      return;
    }
    (async () => {
      try {
        const res = await withRetry(() => axios.get(`${API}/workspaces`));
        setItems(res.data?.items || []);
      } catch {
        toast.error('Could not load classrooms — check your connection and pull to refresh.');
      } finally {
        setLoading(false);
      }
    })();
  }, [user, authLoading, navigate]);

  const create = async (e) => {
    e?.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      const res = await withRetry(() =>
        axios.post(`${API}/workspaces`, { name: name.trim(), type: 'classroom' }),
      );
      toast.success(`Created "${res.data.name}"`);
      navigate(`/classroom/${res.data.workspace_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create classroom — please try again.');
    } finally {
      setCreating(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F1014] text-white">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#B89762]/40 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="classroom-home">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          >
            <Chalkboard size={16} weight="fill" className="text-white" />
          </div>
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#C29D52]">
            For Teachers
          </span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-3">Classrooms.</h1>
        <p className="text-base text-gray-400 max-w-xl mb-10">
          A timeline space for your class. Upload photos and short videos during lessons —
          parents see them on a read-only feed, no sign-up needed.
        </p>

        {/* Create form */}
        <form
          onSubmit={create}
          className="rounded-2xl p-5 mb-10"
          style={{
            background: 'rgba(194,157,82,0.05)',
            border: '1px dashed rgba(194,157,82,0.3)',
          }}
        >
          <label className="block text-xs uppercase tracking-[0.18em] font-bold text-gray-400 mb-2">
            New classroom
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Year 6 — Mr. Phoenix"
              className="flex-1 min-w-0 bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#B89762]/40 transition-colors"
              data-testid="classroom-new-name"
            />
            <button
              type="submit"
              disabled={creating || !name.trim()}
              className="px-5 py-3 rounded-xl text-sm font-bold text-white disabled:opacity-50 transition-all hover:scale-[1.015]"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="classroom-create"
            >
              <Plus size={14} weight="bold" className="inline mr-1" />
              {creating ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>

        {/* List */}
        {items.length === 0 ? (
          <div className="text-sm text-gray-500" data-testid="classroom-empty">
            No classrooms yet. Create your first one above.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((ws) => (
              <button
                key={ws.workspace_id}
                onClick={() => navigate(`/classroom/${ws.workspace_id}`)}
                className="w-full text-left rounded-xl px-5 py-4 flex items-center justify-between transition-all hover:bg-white/5"
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
                data-testid={`classroom-row-${ws.workspace_id}`}
              >
                <div>
                  <div className="font-semibold text-white">{ws.name}</div>
                  <div className="text-xs text-gray-500">Share code · {ws.share_code}</div>
                </div>
                <ArrowRight size={16} weight="bold" className="text-gray-500" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassroomHome;
